import { getDb } from "@/lib/firebase";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
} from "firebase/firestore";

function db() {
  const firestore = getDb();
  if (!firestore) throw new Error("Firestore not available — Firebase not configured");
  return firestore;
}

// Returns true if Firestore is available (Firebase configured + client-side)
function hasFirestore(): boolean {
  try {
    return getDb() !== null;
  } catch {
    return false;
  }
}
import { UserData, UserProfile, UserRole } from "@/types/user";

export class UserService {
  /**
   * Firestore "updateDoc" fails if the document doesn't exist.
   * For this app, users may exist in backend DB without a Firestore `/users/{uid}` doc,
   * so we use an upsert (set with merge) for all partial updates.
   */
  private static async upsertUserDoc(userId: string, data: Record<string, any>): Promise<void> {
    if (!hasFirestore()) return;
    await setDoc(doc(db(), "users", userId), data, { merge: true });
  }

  static async createUser(
    userId: string,
    email: string,
    role: UserRole,
  ): Promise<UserData> {
    console.log("UserService: Creating user document");
    console.log("UserService: User ID:", userId);
    console.log("UserService: Email:", email);
    console.log("UserService: Role:", role);

    try {
      const userData: UserData = {
        uid: userId,
        email,
        role,
        displayName: email.split('@')[0],
        createdAt: new Date(),
        lastLoginAt: new Date(),
        isActive: true,
        profile_image: null, // Initialize profile_image to null or a default value
      };

      console.log("UserService: User data to be created:", userData);

      const userRef = doc(db(), "users", userId);
      console.log("UserService: Document reference created");

      // Check if document already exists
      const docSnap = await getDoc(userRef);
      if (docSnap.exists()) {
        console.log(
          "UserService: Document already exists, returning existing data",
        );
        return docSnap.data() as UserData;
      }

      console.log("UserService: Creating new document");
      await setDoc(userRef, userData);
      console.log("UserService: Document created successfully");

      return userData;
    } catch (error) {
      console.error("UserService: Error creating user document:", error);
      if (error instanceof Error) {
        console.error("UserService: Error details:", {
          code: (error as any).code,
          message: error.message,
          stack: error.stack,
        });
      }
      throw error;
    }
  }

  static async getUser(userId: string): Promise<UserData | null> {
    if (!hasFirestore()) return null;
    try {
      const userDoc = await getDoc(doc(db(), "users", userId));
      if (userDoc.exists()) return userDoc.data() as UserData;
      return null;
    } catch (error) {
      console.warn("UserService: Firestore getUser failed (check rules):", (error as any)?.code);
      return null;
    }
  }

  static async updateUser(uid: string, data: Partial<UserData>): Promise<void> {
    const updateData = {
      ...data,
      updatedAt: new Date()
    };
    await UserService.upsertUserDoc(uid, updateData);
  }

  static async updateUserRole(userId: string, role: UserRole): Promise<void> {
    await UserService.upsertUserDoc(userId, {
      role,
      updatedAt: new Date()
    });
  }

  static async updateUserProfile(
    userId: string,
    profile: Partial<UserProfile>,
  ): Promise<void> {
    await UserService.upsertUserDoc(userId, profile as any);
  }

  static async deactivateUser(userId: string): Promise<void> {
    await UserService.upsertUserDoc(userId, {
      isActive: false,
      deactivatedAt: new Date()
    });
  }

  static async activateUser(userId: string): Promise<void> {
    await UserService.upsertUserDoc(userId, { isActive: true });
  }

  static async getAllUsers(): Promise<UserData[]> {
    const usersQuery = query(collection(db(), "users"));
    const querySnapshot = await getDocs(usersQuery);
    return querySnapshot.docs.map((doc) => doc.data() as UserData);
  }

  static async getUsersByRole(role: UserRole): Promise<UserData[]> {
    const usersQuery = query(
      collection(db(), "users"),
      where("role", "==", role),
    );
    const querySnapshot = await getDocs(usersQuery);
    return querySnapshot.docs.map((doc) => doc.data() as UserData);
  }

  static async updateLastLogin(userId: string): Promise<void> {
    if (!hasFirestore()) return;
    try {
      await UserService.upsertUserDoc(userId, { lastLoginAt: new Date() });
    } catch (error) {
      console.warn("UserService: Firestore updateLastLogin failed (check rules):", (error as any)?.code);
    }
  }

  /**
   * Fetch user data from backend (PostgreSQL) with permissions.
   * Uses single profile API: POST /api/users/get-profile with firebaseuid.
   */
  static async getUserFromBackend(firebaseUid: string): Promise<any> {
    try {
      const response = await fetch('/api/users/get-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ firebaseuid: firebaseUid }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('Token expired or unauthorized');
          return null;
        }
        return null;
      }

      const data = await response.json();
      if (data.success && data.data) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('UserService: Error fetching user from backend:', error);
      return null;
    }
  }

  static async verifyCurrentPassword(firebaseUid: string, password: string): Promise<boolean> {
    try {
      const response = await fetch('/api/users/get-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ firebaseuid: firebaseUid, password }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      // If backend returns success: true, password is correct
      return data.success === true;
    } catch (error) {
      console.error('Error verifying password:', error);
      return false;
    }
  }
}


